<script setup lang="ts">
import { computed, inject } from 'vue'

import { ButtonComponent, FlexComponent, PlatformIconComponent, ProfileImageComponent, TooltipComponent, TypographyComponent } from '../../'
import RemoveIcon from '../../../assets/icons/remove.svg?component'
import { formatterProviderKey } from '../../../constants'
import { defaultFormatter } from '../../../formatters'
import { type GetSegmentCreatorLink, type SegmentCreator, SegmentCreatorValueKindEnum } from '../../../types'
import { isInternalLink } from '../../../utils'

import { formatSegmentCreatorValue } from './SegmentPreview.helpers'

const props = withDefaults(
  defineProps<{
    creator: SegmentCreator
    isRemovable?: boolean
    getCreatorLink?: GetSegmentCreatorLink | null
  }>(),
  { isRemovable: false, getCreatorLink: null },
)
const emits = defineEmits<{ remove: [creator: SegmentCreator] }>()
const { formatMoney } = inject(formatterProviderKey, defaultFormatter)

const VALUE_SEPARATOR = '•'

const displayName = computed(() => props.creator.username || `Creator ${props.creator.id}`)

const matchedValues = computed(() =>
  props.creator.matchedValues.map((value) => ({
    label: formatSegmentCreatorValue(value, formatMoney),
    ...(value.kind === SegmentCreatorValueKindEnum.Platform
      ? { platform: value.platform, handle: value.handle, isConnected: value.isConnected }
      : { platform: null, handle: null, isConnected: false }),
  })),
)

const creatorLink = computed(() => props.getCreatorLink?.(props.creator) ?? null)

const creatorLinkComponent = computed(() => {
  if (!creatorLink.value) {
    return 'div'
  }

  return isInternalLink(creatorLink.value) ? 'router-link' : 'a'
})

const creatorLinkProps = computed(() => {
  switch (creatorLinkComponent.value) {
    case 'router-link':
      return { to: creatorLink.value }
    case 'a':
      return { href: creatorLink.value }
    default:
      return {}
  }
})
</script>

<template>
  <FlexComponent align="center" :gap="16" class="segment-preview-item" :class="{ 'segment-preview-item--removable': isRemovable }">
    <component
      :is="creatorLinkComponent"
      v-bind="creatorLinkProps"
      class="segment-preview-item__info"
      :class="{ 'segment-preview-item__info--link': creatorLink }"
    >
      <ProfileImageComponent :image="creator.image ?? undefined" :name="displayName" :alt="displayName" :size="32" />
      <div class="segment-preview-item__data">
        <TypographyComponent variant="title-medium" text-truncate>
          {{ displayName }}
        </TypographyComponent>
        <FlexComponent v-if="matchedValues.length > 0" align="center" :gap="8" class="segment-preview-item__criteria">
          <template v-for="(matched, index) in matchedValues" :key="index">
            <!-- Decorative: the values are already separate nodes, so a screen reader gains nothing from the bullet. -->
            <TypographyComponent v-if="index > 0" variant="label-small" aria-hidden="true" class="segment-preview-item__separator">
              {{ VALUE_SEPARATOR }}
            </TypographyComponent>
            <FlexComponent align="center" :gap="4" class="segment-preview-item__value">
              <TooltipComponent
                v-if="matched.platform"
                :disabled="!matched.handle"
                placement="bottom"
                effect="light"
                :show-arrow="false"
                class="segment-preview-item__platform"
              >
                <PlatformIconComponent :platform="matched.platform" :is-connected="matched.isConnected" :size="14" />
                <template #content>
                  <TypographyComponent variant="label-small">{{ matched.handle }}</TypographyComponent>
                </template>
              </TooltipComponent>
              <TypographyComponent v-if="matched.label" variant="label-small">
                {{ matched.label }}
              </TypographyComponent>
            </FlexComponent>
          </template>
        </FlexComponent>
      </div>
    </component>

    <ButtonComponent
      v-if="isRemovable"
      variant="text"
      is-icon
      :icon-size="18"
      :aria-label="`Remove ${displayName}`"
      class="segment-preview-item__remove"
      @click="emits('remove', creator)"
    >
      <RemoveIcon />
    </ButtonComponent>
  </FlexComponent>
</template>

<style scoped lang="scss">
.segment-preview-item {
  height: 64px;
  padding: 16px 5px 16px 16px;

  border: 1px solid var(--button-hover);
  border-radius: 16px;
  background: var(--inverted);

  transition: background-color var(--transition) ease;

  &--removable:hover,
  &--removable:focus-within {
    background: var(--item-hover);

    .segment-preview-item__remove {
      opacity: 1;
      pointer-events: auto;
    }
  }
}

.segment-preview-item__info {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;

  color: inherit;
  text-decoration: none;
}

.segment-preview-item__data {
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-width: 0;
}

.segment-preview-item__criteria {
  min-width: 0;
  opacity: 0.48;
}

.segment-preview-item__value {
  white-space: nowrap;
}

.segment-preview-item__separator {
  flex-shrink: 0;
}

/* TooltipComponent's wrapper is a plain block, which would knock the icon off the text baseline. */
.segment-preview-item__platform {
  display: flex;
  align-items: center;
}

.button.segment-preview-item__remove {
  flex-shrink: 0;
  height: 32px;
  width: 32px;
  min-height: 32px;
  padding: 8px;
  border-radius: 48px;

  opacity: 0;
  pointer-events: none;

  &:hover,
  &:focus-visible {
    color: var(--error);
  }

  &:focus-visible {
    opacity: 1;
    pointer-events: auto;
  }
}

:deep(.el-icon) {
  path {
    fill: currentColor;
  }
}
</style>
